import { styled } from '@mui/system';
import COLORS from '../../../theme/Colors';
import FONTS_FAMILY from '../../../theme/FontsFamily';
import { BodyText } from '../../../theme/Styles';
import { Button, Grid, Box } from '@mui/material';

export const PageWrapper = styled('div')({
    backgroundColor: 'transparent',
    minHeight: '100vh',
    padding: '1.5rem 0 4rem 0',
    '@media (max-width: 768px)': {
        padding: '1rem 3%',
    }
});

export const MainGrid = styled(Grid)({
    marginTop: '1rem',
});

// Top bar sharing icons
export const SocialShareContainer = styled('div')({
    display: 'flex',
    gap: '8px',
    marginBottom: '1rem',
});

export const SocialButton = styled('div')({
    width: '32px',
    height: '32px',
    backgroundColor: '#007BFF',
    color: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': {
        opacity: 0.8,
    },
    '& svg': {
        fontSize: '18px',
    }
});

// Main Content Card
export const ServiceCard = styled(Box)({
    backgroundColor: COLORS.white,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    padding: '2.5rem',
    '@media (max-width: 768px)': {
        padding: '1.5rem',
    },
    'html.dark &': {
        backgroundColor: '#000000',
    }
});

export const ServiceTitle = styled('h2')({
    fontFamily: FONTS_FAMILY.bold.title,
    color: '#202A5D',
    fontSize: '28px',
    marginBottom: '1.5rem',
    fontWeight: 700,
});

// Tabs System
export const TabsWrapper = styled('div')({
    display: 'flex',
    backgroundColor: '#F1F3F9',
    borderRadius: '6px',
    padding: '4px',
    marginBottom: '2rem',
    'html.dark &': {
        backgroundColor: '#0a0a0a',
    }
});

export const TabItem = styled('button', {
    shouldForwardProp: (prop) => prop !== 'active',
})((props) => ({
    flex: 1,
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: FONTS_FAMILY.regular.body,
    backgroundColor: props.active ? '#202A5D' : 'transparent',
    color: props.active ? COLORS.white : '#4A5568',
    '&:hover': {
        backgroundColor: props.active ? '#202A5D' : '#E2E8F0',
    },
    'html.dark &': {
        color: props.active ? COLORS.white : '#94a3b8',
    },
    'html.dark &:hover': {
        backgroundColor: props.active ? '#202A5D' : '#151515',
    }
}));

// Small Sections Headers
export const SectionHeader = styled('h3')({
    fontFamily: 'Merriweather, Georgia, serif',
    color: 'rgb(29, 40, 98)',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '28px',
    fontStyle: 'normal',
    marginBottom: '1.5rem',
    marginTop: '0.5rem',
});

export const DescriptionText = styled('p')({
    fontFamily: FONTS_FAMILY.regular.body,
    color: '#4A5568',
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '1.5rem',
});

// Info Grid (Orange accent)
export const InfoBlockContainer = styled(Grid)({
    marginTop: '2rem',
    marginBottom: '2rem',
});

export const InfoItem = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '0.5rem 1rem',
    position: 'relative',
    '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '10%',
        height: '80%',
        width: '4px',
        backgroundColor: COLORS.primary, // #EA361A
        borderRadius: '2px',
    }
});

export const InfoIconWrapper = styled('div')({
    color: COLORS.primary,
    '& svg': {
        fontSize: '24px',
    }
});

export const InfoTextWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

export const InfoLabel = styled('span')({
    fontWeight: 700,
    fontSize: '15px',
    color: '#2D3748',
    fontFamily: FONTS_FAMILY.bold.body,
});

export const InfoValue = styled('span')({
    fontSize: '14px',
    color: '#718096',
    marginTop: '4px',
    fontFamily: FONTS_FAMILY.regular.body,
});

// Note Box
export const NoteBox = styled('div')({
    backgroundColor: '#EDF2F7',
    padding: '1.5rem',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#4A5568',
    marginTop: '2rem',
    fontFamily: FONTS_FAMILY.regular.body,
    lineHeight: '1.5',
    'html.dark &': {
        backgroundColor: '#0a0a0a',
    }
});

// Sidebar Components
export const SidebarCard = styled(Box)({
    backgroundColor: '#EDF2F7',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    'html.dark &': {
        backgroundColor: '#0a0a0a',
    }
});

export const SidebarTitle = styled('h4')({
    fontFamily: FONTS_FAMILY.bold.title,
    color: 'rgb(29, 40, 98)',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '28px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

export const ContactItem = styled('div')({
    display: 'flex',
    gap: '12px',
    marginBottom: '1.2rem',
    '& svg': {
        color: COLORS.primary,
        fontSize: '20px',
        marginTop: '2px',
    }
});

export const ContactLabel = styled('div')({
    fontWeight: 700,
    fontSize: '13px',
    color: '#2D3748',
    textTransform: 'uppercase',
    marginBottom: '2px',
    fontFamily: FONTS_FAMILY.bold.title,
});

export const ContactValue = styled('div')({
    fontSize: '16px',
    color: 'rgb(57, 79, 106)',
    lineHeight: '26px',
    fontWeight: 400,
    fontFamily: FONTS_FAMILY.regular.body,
});

export const ContactButton = styled(Button)({
    width: '100%',
    backgroundColor: COLORS.white,
    color: '#4A5568',
    textTransform: 'none',
    fontWeight: 600,
    border: '1px solid #E2E8F0',
    marginTop: '1rem',
    '&:hover': {
        backgroundColor: '#F8F9FA',
    },
    'html.dark &': {
        backgroundColor: '#000000',
        borderColor: '#222222',
    },
    'html.dark &:hover': {
        backgroundColor: '#111111',
    }
});

// Related Services List
export const RelatedServiceItem = styled('div')({
    backgroundColor: '#F1F3F9',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.2rem',
    border: '1px solid #E2E8F0',
    transition: 'all 0.2s',
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 15px rgba(0,0,0,0.05)',
        backgroundColor: '#FFFFFF',
    },
    'html.dark &': {
        backgroundColor: '#0a0a0a',
        borderColor: '#222222',
    },
    'html.dark &:hover': {
        backgroundColor: '#111111',
    }
});

export const RelatedTitle = styled('h5')({
    fontSize: '17px',
    fontWeight: 700,
    color: '#202A5D',
    marginBottom: '12px',
    lineHeight: '1.4',
    fontFamily: FONTS_FAMILY.bold.title,
});

export const RelatedDescription = styled('p')({
    fontSize: '14px',
    color: '#4A5568',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    fontFamily: FONTS_FAMILY.regular.body,
});

export const RelatedLink = styled('a')({
    color: 'rgb(255, 56, 20)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
        textDecoration: 'underline',
        opacity: 0.8,
    }
});

export const ActionButton = styled(Button)({
    width: '100%',
    backgroundColor: '#202A5D',
    color: COLORS.white,
    padding: '12px',
    borderRadius: '6px',
    marginTop: '2rem',
    fontWeight: 600,
    fontSize: '16px',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#151B3D',
    }
});

export const BackButton = styled(Button)({
    marginTop: '2rem',
    color: '#4A5568',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    border: '1px solid #E2E8F0',
    padding: '8px 16px',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    '&:hover': {
        backgroundColor: '#F8FAFC',
        borderColor: '#CBD5E1',
    },
    'html.dark &': {
        backgroundColor: '#000000',
        borderColor: '#222222',
    },
    'html.dark &:hover': {
        backgroundColor: '#111111',
        borderColor: '#333333',
    }
});

// Breadcrumb
export const BreadcrumbWrapper = styled('div')({
    backgroundColor: '#F1F5F9',
    borderBottom: '1px solid #E2E8F0',
    width: '100vw',
    position: 'relative',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
    marginTop: '-70px', // Counteract LayoutBodyDivider from AppLayout
    zIndex: 10,
});

export const BreadcrumbInner = styled('div')({
    width: '85%',
    maxWidth: '1920px',
    margin: '0 auto',
    padding: '15px 1.5rem', // 1.5rem matches logo marginLeft
    display: 'flex',
    alignItems: 'center',
});

export const BreadcrumbContent = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: 'rgb(62, 63, 65)',
    textTransform: 'uppercase',
});

export const BreadcrumbLink = styled('span')({
    cursor: 'pointer',
    color: 'inherit',
    '&:hover': {
        textDecoration: 'underline',
    }
});

export const BreadcrumbSeparator = styled('span')({
    margin: '0 4px',
    color: '#94A3B8',
    fontWeight: 400,
});

export const BreadcrumbCurrent = styled('span')({
    color: 'inherit',
});

// Social Share Grid
export const SocialShareGrid = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
});

export const SocialShareIcon = styled('div')({
    width: '40px',
    height: '40px',
    backgroundColor: '#007BFF',
    color: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        backgroundColor: '#0056b3',
    }
});

export const DownloadButton = styled('button')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#FFFFFF',
    color: '#FF3814',
    border: '1px solid #FF3814',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: '#FF3814',
        color: 'white',
    },
    '& svg': {
        fontSize: '18px',
    },
    'html.dark &': {
        backgroundColor: '#000000',
        color: '#FF3814',
    },
    'html.dark &:hover': {
        backgroundColor: '#FF3814',
        color: '#000000',
    }
});